import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Package,
  Warehouse,
  Factory,
  ShoppingCart,
  GanttChart,
  ClipboardCheck,
  Coins,
  Menu,
  X,
  LogOut,
  User,
  Moon,
  Sun,
  ChevronDown,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { useInventory } from '@/context/InventoryContext';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { IdleTimeoutWarning } from '@/components/auth/IdleTimeoutWarning';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Products', href: '/products', icon: Package },
  { name: 'Work Order', href: '/work-order', icon: Factory },
  { name: 'Procurement Expenses', href: '/procurement', icon: ShoppingCart },
  { name: 'Inventory & Planning', href: '/inventory', icon: Warehouse },
  { name: 'Gantt View', href: '/gantt', icon: GanttChart },
  { name: 'Quality', href: '/quality', icon: ClipboardCheck },
  { name: 'Project Profit', href: '/cashflow', icon: Coins },
];

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { isLoading } = useInventory();

  // Idle timeout - logs out user after inactivity
  const { isWarning, remainingSeconds, resetTimer } = useIdleTimeout({
    idleTimeout: 30 * 60 * 1000, // 30 minutes
    warningTime: 2 * 60 * 1000,  // 2 minutes warning
    enabled: true,
  });

  // Apply dark mode on initial mount
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Get current page title
  const currentPage = navigation.find((item) =>
    location.pathname.startsWith(item.href)
  );

  return (
    <>
      {/* Global Loading Screen */}
      <LoadingScreen isLoading={isLoading} />

      {/* Idle Timeout Warning */}
      <IdleTimeoutWarning
        isOpen={isWarning}
        remainingSeconds={remainingSeconds}
        onStayLoggedIn={resetTimer}
      />

      <div className="min-h-screen bg-background">
        {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Modern Glassmorphism Design */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-out lg:translate-x-0',
          // Flex layout for proper content positioning
          'flex flex-col',
          // Glassmorphism background
          'bg-gradient-to-b from-white/90 to-white/70',
          'dark:from-slate-900/95 dark:to-slate-800/90',
          'backdrop-blur-xl',
          // Border and shadow
          'border-r border-white/20 dark:border-white/10',
          'shadow-[4px_0_24px_rgba(0,0,0,0.06)]',
          'dark:shadow-[4px_0_24px_rgba(0,0,0,0.3)]',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />

        {/* Logo */}
        <div className="relative flex items-center justify-between border-b border-white/10 px-6 py-3">
          <div className="flex flex-col items-center">
            <img src="/pos-logo.png" alt="POS Logo" className="h-20 w-auto" />
            <span className="text-xs font-bold text-foreground mt-1">Thermalink Project Management</span>
          </div>
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 overflow-y-auto space-y-1 px-3 py-4">
          {navigation.map((item) => {
            const isActive = location.pathname.startsWith(item.href);
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={cn(
                  'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-gradient-to-r from-primary/90 to-primary text-primary-foreground shadow-lg shadow-primary/25'
                    : 'text-muted-foreground hover:bg-white/50 dark:hover:bg-white/10 hover:text-foreground hover:translate-x-1'
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <div className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
                  isActive
                    ? 'bg-white/20'
                    : 'bg-slate-100 dark:bg-slate-800 group-hover:bg-primary/10 group-hover:scale-110'
                )}>
                  <item.icon className={cn(
                    'h-4 w-4 transition-colors',
                    isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-primary'
                  )} />
                </div>
                {item.name}
              </NavLink>
            );
          })}

          {/* Admin Panel - Only visible to admins */}
          {user?.role === 'admin' && (
            <>
              <div className="my-4 border-t border-white/10" />
              <NavLink
                to="/admin"
                className={cn(
                  'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                  location.pathname === '/admin'
                    ? 'bg-gradient-to-r from-primary/90 to-primary text-primary-foreground shadow-lg shadow-primary/25'
                    : 'text-muted-foreground hover:bg-white/50 dark:hover:bg-white/10 hover:text-foreground hover:translate-x-1'
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <div className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
                  location.pathname === '/admin'
                    ? 'bg-white/20'
                    : 'bg-slate-100 dark:bg-slate-800 group-hover:bg-primary/10 group-hover:scale-110'
                )}>
                  <Settings className={cn(
                    'h-4 w-4 transition-colors',
                    location.pathname === '/admin' ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-primary'
                  )} />
                </div>
                Admin Panel
              </NavLink>
            </>
          )}
        </nav>

        {/* User section */}
        <div className="relative shrink-0 border-t border-white/10 p-4">
          <div className={cn(
            'flex items-center gap-3 p-3 rounded-xl',
            'bg-gradient-to-r from-white/50 to-white/30',
            'dark:from-white/10 dark:to-white/5',
            'border border-white/20 dark:border-white/10'
          )}>
            <div className={cn(
              'h-10 w-10 rounded-xl flex items-center justify-center',
              'bg-gradient-to-br from-primary/20 to-primary/10',
              'border border-primary/20'
            )}>
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">
                {user?.full_name || 'User'}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email || ''}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 lg:px-6">
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Breadcrumb / Page title */}
          <div className="flex-1">
            <h1 className="text-lg font-semibold">
              {currentPage?.name || 'Dashboard'}
            </h1>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-md hover:bg-muted transition-colors"
            >
              {darkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors"
              >
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <ChevronDown className="h-4 w-4" />
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 rounded-md border bg-popover shadow-lg z-50">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          logout();
                          setUserMenuOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
    </>
  );
}
